import { useState } from 'react';
import { Link } from 'react-router-dom';
import EarningsSummaryCards from './components/EarningsSummaryCards';
import EarningsBreakdown from './components/EarningsBreakdown';
import JobEarningsTable from './components/JobEarningsTable';
import JobDetailDrawer from './components/JobDetailDrawer';

export default function ProviderEarningsPage() {
  const [timeframe, setTimeframe] = useState<'today' | '7days' | '30days' | 'custom'>('30days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const getTimeframeRange = () => {
    const now = new Date();
    const to = now.toISOString();
    let from: string;

    switch (timeframe) {
      case 'today':
        from = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        break;
      case '7days':
        from = new Date(now.setDate(now.getDate() - 7)).toISOString();
        break;
      case '30days':
        from = new Date(now.setDate(now.getDate() - 30)).toISOString();
        break;
      case 'custom':
        from = customFrom ? new Date(customFrom).toISOString() : new Date(now.setDate(now.getDate() - 30)).toISOString();
        return { from, to: customTo ? new Date(customTo).toISOString() : to };
      default:
        from = new Date(now.setDate(now.getDate() - 30)).toISOString();
    }

    return { from, to };
  };

  const range = getTimeframeRange();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link
                to="/provider-dashboard"
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-2"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Dashboard
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">Earnings</h1>
              <p className="text-gray-600 mt-1">Track your earnings and job history</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Timeframe Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Timeframe:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTimeframe('today')}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                  timeframe === 'today'
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setTimeframe('7days')}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                  timeframe === '7days'
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                7 days
              </button>
              <button
                onClick={() => setTimeframe('30days')}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                  timeframe === '30days'
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                30 days
              </button>
              <button
                onClick={() => setTimeframe('custom')}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                  timeframe === 'custom'
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Custom
              </button>
            </div>
            {timeframe === 'custom' && (
              <div className="flex flex-wrap items-center gap-2 ml-auto">
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <EarningsSummaryCards timeframe={range} />

        {/* Earnings Breakdown */}
        <div className="mt-6">
          <EarningsBreakdown timeframe={range} />
        </div>

        {/* Job Earnings Table */}
        <div className="mt-6">
          <JobEarningsTable timeframe={range} onViewDetails={setSelectedJobId} />
        </div>
      </div>

      {/* Job Detail Drawer */}
      <JobDetailDrawer jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
    </div>
  );
}
